import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';
import { Children, isValidElement } from 'react';
import { cn } from '@/lib/utils';
import { Icon } from '@/ui/icons/Icon';
import { Spinner } from '@/ui/shadcn/spinner';

// shadcn/ui (AD #39): shadcn's Button, adapted to the CRM's rules — no own focus ring (the global
// :focus-visible ring applies), `disabled` is `aria-disabled` so the button stays focusable and can say
// why (design-system §5, §9), the disabled look is the secondary fill rather than opacity, and `busy`
// shows a spinner with `aria-busy` and refuses the press, as the §5 Busy row requires. The spinner is shadcn's
// own pattern (design-system Rev 83): shadcn's `Spinner`, in the flow before the label with the button's gap,
// as any leading icon is, so it is spaced like one; a button whose first child is an icon (an icon-only button
// included) shows the spinner in the icon's place, so it keeps its width, and any other button grows by the
// spinner and its gap while it waits. Whether the icon leads is read from the children, because `+` in CSS
// skips the label's text node and would take a trailing icon for a leading one. Drawn on a link (`asChild`), a
// button has no underline and every variant names its text
// colour: the app loads no Tailwind reset, so otherwise an outline or ghost link kept the browser's blue
// underlined link text.
const buttonVariants = cva(
    "[&>[data-leading-icon]+svg]:hidden relative inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap no-underline transition-all aria-disabled:cursor-not-allowed aria-disabled:border-secondary aria-disabled:bg-secondary aria-disabled:text-muted-foreground aria-disabled:shadow-none aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground hover:bg-primary/90',
                destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                // BUG-157: the trigger of a destructive action (Deactivate, Delete, Withdraw) — `outline`'s
                // card fill, border, shadow and sizes in the destructive colour, with a light destructive wash
                // on hover; quieter than the solid `destructive` confirm it opens. Disabled is the shared look.
                'destructive-outline':
                    'border border-destructive bg-card text-destructive shadow-xs hover:bg-destructive/10',
                outline:
                    'border border-input bg-card text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground',
                secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
                ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
                link: 'text-primary underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-9 px-4 py-2 has-[>svg]:px-3',
                xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
                sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
                lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
                icon: 'size-9',
                'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
                'icon-sm': 'size-8',
                'icon-lg': 'size-10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

type ButtonProps = React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean;
        /**
         * Shows a spinner (not with `asChild`), sets `aria-busy` and refuses the press; the label stays, a
         * leading icon gives way to the spinner, and a button with no icon grows by the spinner and its gap.
         */
        busy?: boolean;
    };

function Button({
    className,
    variant = 'default',
    size = 'default',
    asChild = false,
    disabled = false,
    busy = false,
    type = 'button',
    onClick,
    children,
    ...props
}: ButtonProps) {
    const Comp = asChild ? Slot.Root : 'button';
    const refused = disabled || busy;

    return (
        <Comp
            /* testid-exempt: a shadcn primitive; the caller passes data-testid, a label or text through props */
            data-slot="button"
            data-variant={variant}
            data-size={size}
            className={cn(buttonVariants({ variant, size, className }))}
            {...(asChild ? {} : { type })}
            // Both always present, as the src/ui Button renders them: "false" while enabled or idle, so a test or
            // a reader can tell enabled from never-disableable, and a finished wait from no wait at all.
            aria-disabled={disabled}
            aria-busy={busy}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                if (refused) {
                    event.preventDefault();
                    return;
                }
                onClick?.(event);
            }}
            {...props}
        >
            {/* With `asChild`, Slot takes exactly one child to merge onto, so the spinner is not offered
                beside it: a `null` sibling still counts as a second child, and Slot refuses (BUG-201). */}
            {asChild ? (
                children
            ) : (
                <>
                    {busy ? <ButtonSpinner leadsIcon={leadsWithIcon(children)} size={size} /> : null}
                    {children}
                </>
            )}
        </Comp>
    );
}

function isIcon(child: React.ReactNode): boolean {
    return isValidElement(child) && (child.type === Icon || child.type === 'svg');
}

/**
 * Whether the button's first child is an icon. Only a direct child counts, and only the registry's `Icon` or a bare
 * `<svg>`: `Children.toArray` does not look into a Fragment or a wrapping element, so pass icons as direct `Icon`
 * children.
 */
function leadsWithIcon(children: React.ReactNode): boolean {
    return isIcon(Children.toArray(children)[0]);
}

/**
 * The busy spinner, added before the children, which are never re-rendered in another shape: a label node removed
 * and added back would move focus in a Radix dialog whose focus trap sees a node leave while nothing has focus. In
 * front of a leading icon it carries `data-leading-icon`, and the button's `[&>[data-leading-icon]+svg]:hidden`
 * takes that icon out of the flow, so the spinner stands in its place. It is drawn only: the button's `aria-busy`
 * says it is waiting, and its name stays the label.
 */
function ButtonSpinner({
    leadsIcon,
    size,
}: {
    readonly leadsIcon: boolean;
    readonly size: ButtonProps['size'];
}) {
    // The size of the icon it stands beside: the xs sizes draw `size-3` icons, every other size `size-4`.
    const small = size === 'xs' || size === 'icon-xs';
    return (
        <Spinner
            aria-hidden="true"
            className={small ? 'size-3' : undefined}
            data-leading-icon={leadsIcon ? '' : undefined}
            data-slot="button-spinner"
        />
    );
}

export { Button, buttonVariants };
